import { Elements } from '@stripe/react-stripe-js';
import React, { type PropsWithChildren, createContext, useContext, useEffect, useMemo, useState } from 'react';

import { loadStripe } from '@stripe/stripe-js';

import type {
  BillingContact,
  BillingInterval,
  BillingModeType,
  PersistedStripePrice,
  SanitizedCustomer,
  SanitizedSelfServeSubscription,
  SanitizedStripeSource,
  SanitizedSubscription,
} from '@sine/sine-core-api-types';
import type { Team } from '@sine/sine-team-switcher';
import { type ISelectOption, sortItem } from '@sine/sine-ui-components';

import { BillingIntervalValues } from '../../services/billing/billing.constants';
import {
  BillingMode,
  type Country,
  type Currency,
  type HydratedBillableSite,
  LICENSE_TYPE,
  type PlanSelectOption,
} from '../../services/billing/billing.types';
import { useFeatures } from '../../shared/customHooks/useFeatures';
import { getExistingPlanOptions, isSelfServeSubscription } from './billing.helpers';
import { SiteStatus, allCurrencyOptions, country2currency, currencies } from './constants/billing.constants';
import {
  useBillingContact,
  useBillingMode,
  useBillingSites,
  useCustomer,
  usePrices,
  useSiteStatistics,
  useSubscriptions,
} from './hooks/billing.hooks';

const stripePromise = loadStripe(window.env.REACT_APP_STRIPE_CLIENT_KEY);

interface BillingContextValues {
  isImpersonating: boolean;
  isLoading: boolean;
  currentTeam: Team;
  billingContact: BillingContact | undefined;
  billingMode: `${BillingModeType}` | Array<never>;
  customer: SanitizedCustomer | null | undefined;
  currencyOptions: Array<ISelectOption>;
  existingPlanOptions: Array<PlanSelectOption>;
  dateOptions: Array<ISelectOption> | undefined;
  hasPresetCurrency: boolean;
  customerCurrency: Currency;
  billingCycle: BillingInterval;
  cards: Array<SanitizedStripeSource>;
  defaultCard: SanitizedStripeSource | undefined;
  subscriptions: Array<SanitizedSubscription>;
  sites: Array<HydratedBillableSite>;
  licencedSites: Array<HydratedBillableSite>;
  unlicencedSites: Array<HydratedBillableSite>;
  prices: Array<PersistedStripePrice>;
  trials: Array<SanitizedSubscription>;
  activeStripeSubscription: SanitizedSelfServeSubscription | undefined;
  isBillingEnabled: boolean;
  isBilledManually: boolean;
  refetchCustomer: () => void;
  refetchSubscriptions: () => void;
  refetchSites: () => void;
  refetchBillingContact: () => void;
  refetchAllData: () => void;
  refetchBillingMode: () => void;
  setSelectedCurrency: (currency: Currency) => void;
  setBillingCycle: (billingCycle: BillingInterval) => void;
}

const defaultBillingContext: BillingContextValues = {
  isImpersonating: false,
  isLoading: false,
  currentTeam: { id: '', name: '' },
  billingContact: undefined,
  customer: undefined,
  billingMode: [],
  currencyOptions: [],
  existingPlanOptions: [],
  dateOptions: [],
  hasPresetCurrency: false,
  customerCurrency: 'aud',
  billingCycle: BillingIntervalValues.YEARLY,
  cards: [],
  defaultCard: undefined,
  subscriptions: [],
  sites: [],
  licencedSites: [],
  unlicencedSites: [],
  prices: [],
  trials: [],
  activeStripeSubscription: undefined,
  isBillingEnabled: true,
  isBilledManually: false,
  refetchCustomer: () => {},
  refetchSubscriptions: () => {},
  refetchSites: () => {},
  refetchBillingContact: () => {},
  refetchAllData: () => {},
  refetchBillingMode: () => {},
  setSelectedCurrency: () => {},
  setBillingCycle: () => {},
};

const BillingContext = createContext(defaultBillingContext);

type BillingProviderProps = PropsWithChildren<{
  isImpersonating: boolean;
  currentTeam: Team;
}>;

export default function BillingProvider({ isImpersonating, currentTeam, children }: BillingProviderProps) {
  const { features } = useFeatures();
  const isBillingV2Enabled = features.includes('BILLING_V2');

  // Local state for currency (needed for price fetching)
  const [customerCurrency, setCustomerCurrency] = useState<Currency>('aud');
  const [selectedCurrency, setSelectedCurrency] = useState<Currency>();

  // Local state for billing cycle
  const [billingCycle, setBillingCycle] = useState<BillingInterval>(BillingIntervalValues.YEARLY);

  // React Query hooks for data fetching
  const { isLoadingCustomer, customer, refetchCustomer } = useCustomer(currentTeam.id);
  const {
    isLoadingSubscriptions,
    subscriptions: _subscriptions,
    refetchSubscriptions,
  } = useSubscriptions(currentTeam.id);
  const { isLoadingBillingSites, billingSites, refetchBillingSites } = useBillingSites(currentTeam.id);
  const { isLoadingSiteStatistics, siteStatistics, refetchSiteStatistics } = useSiteStatistics(currentTeam.id);
  const { isLoadingBillingContact, billingContact, refetchBillingContact } = useBillingContact(currentTeam.id);
  const { isLoadingPrices, prices, refetchPrices } = usePrices(currentTeam.id, customerCurrency, !isBillingV2Enabled);
  const { isLoadingBillingMode, billingMode, refetchBillingMode } = useBillingMode(currentTeam.id);

  // Combined loading state from all queries
  const isLoading =
    isLoadingBillingMode ||
    isLoadingCustomer ||
    isLoadingSubscriptions ||
    isLoadingBillingSites ||
    isLoadingSiteStatistics ||
    isLoadingBillingContact ||
    isLoadingPrices;

  const cards = useMemo(() => customer?.externalData.sources.data || [], [customer]);
  const defaultCard = useMemo(() => customer?.externalData.default_source, [customer]);

  const isBillingEnabled = useMemo(() => billingMode !== BillingMode.OFF, [billingMode]);

  // Process subscriptions - split into trials and regular subscriptions
  const { subscriptions, trials } = useMemo(() => {
    const trialLicenceTypes = [
      `${LICENSE_TYPE.TRIAL_BASIC}`,
      `${LICENSE_TYPE.TRIAL_SYSADMIN}`,
      `${LICENSE_TYPE.TRIAL_30_DAYS}`,
    ];

    const sorted = [..._subscriptions].sort((a, b) => {
      const aDate = a.type === 'stripe' ? a.created : a.valid_from;
      const bDate = b.type === 'stripe' ? b.created : b.valid_from;
      return sortItem(bDate as string, aDate as string);
    });

    const trialsArr: Array<SanitizedSubscription> = [];
    const subscriptionsArr: Array<SanitizedSubscription> = [];

    for (const subscription of sorted) {
      if (subscription.licences?.some((licence) => trialLicenceTypes.includes(licence.type || ''))) {
        trialsArr.push(subscription);
      } else {
        subscriptionsArr.push(subscription);
      }
    }

    return { subscriptions: subscriptionsArr, trials: trialsArr };
  }, [_subscriptions]);

  // Hydrate sites with statistics
  const sites = useMemo<Array<HydratedBillableSite>>(() => {
    return billingSites.map((site) => ({
      ...siteStatistics.find((s) => s.id === site.id)!,
      ...site,
    }));
  }, [billingSites, siteStatistics]);

  // Derive licensed and unlicensed sites
  const licencedSites = useMemo(() => sites.filter((site) => !!site.licence), [sites]);

  const unlicencedSites = useMemo(
    () =>
      sites.filter(
        (site) => !site.licence && (site.status === SiteStatus.ENABLED || site.status === SiteStatus.DISABLED),
      ),
    [sites],
  );

  // Derive currency options and preset currency
  const { currencyOptions, hasPresetCurrency } = useMemo(() => {
    const existingCurrency =
      customer?.externalData.currency || subscriptions.filter(isSelfServeSubscription)[0]?.externalData?.plan?.currency;

    return {
      currencyOptions: allCurrencyOptions.sort((a, b) => sortItem(a.label, b.label)),
      hasPresetCurrency: !!existingCurrency,
    };
  }, [customer, subscriptions]);

  // Update customer currency when customer/subscriptions/sites change
  useEffect(() => {
    const existingCurrency =
      customer?.externalData.currency ||
      // In the case of an outdated customer object from Stripe, or
      // webhook event 'customer.updated' not properly delivered to Sine
      // endpoint, we'll rely on the first existing subscription's plan
      // currency to avoid showing the currency dropdown, which allegedly
      // prevents "multiple currency conflicts errors" from Stripe.
      subscriptions.filter(isSelfServeSubscription)[0]?.externalData?.plan?.currency;

    if (existingCurrency) {
      setCustomerCurrency(existingCurrency);
    } else if (selectedCurrency) {
      setCustomerCurrency(selectedCurrency);
    } else if (sites.length > 0) {
      const firstSiteCountryCode = (sites[0]?.countryCode || 'US').toLowerCase() as Country;
      const isSupportedCountryCode = Object.keys(country2currency).includes(firstSiteCountryCode);
      const countryCode = isSupportedCountryCode ? firstSiteCountryCode : currencies['usd'].country;
      setCustomerCurrency(country2currency[countryCode]);
    }
  }, [customer, subscriptions, sites, selectedCurrency]);

  // Derive existing plan options and date options
  const { existingPlanOptions, dateOptions } = useMemo(() => {
    if (sites.length === 0) {
      return { existingPlanOptions: [] as Array<PlanSelectOption>, dateOptions: [] as Array<ISelectOption> };
    }
    const { allOptions, renewDateOptions } = getExistingPlanOptions(subscriptions, sites);
    return { existingPlanOptions: allOptions, dateOptions: renewDateOptions };
  }, [subscriptions, sites]);

  // Derive active stripe subscription
  const activeStripeSubscription = useMemo(() => subscriptions.find((s) => s.type === 'stripe'), [subscriptions]);

  const isBilledManually = useMemo(() => {
    const trialLicenceTypes = new Set([
      `${LICENSE_TYPE.TRIAL_BASIC}`,
      `${LICENSE_TYPE.TRIAL_SYSADMIN}`,
      `${LICENSE_TYPE.TRIAL_30_DAYS}`,
    ]);

    return (
      subscriptions.length === 1 &&
      subscriptions.every(
        (subscription) =>
          subscription.type === 'manual' &&
          subscription.licences?.some((licence) => !trialLicenceTypes.has(licence.type || '')),
      )
    );
  }, [subscriptions]);

  const refetchAllData = () => {
    void refetchCustomer();
    void refetchSubscriptions();
    void refetchBillingSites();
    void refetchSiteStatistics();
    void refetchBillingContact();
    void refetchPrices();
  };

  const context: BillingContextValues = {
    isImpersonating,
    isLoading,
    currentTeam,
    billingContact,
    billingMode,
    customer,
    currencyOptions,
    dateOptions,
    hasPresetCurrency,
    customerCurrency,
    billingCycle,
    existingPlanOptions,
    cards,
    defaultCard,
    subscriptions,
    sites,
    licencedSites,
    unlicencedSites,
    prices,
    trials,
    activeStripeSubscription,
    isBillingEnabled,
    isBilledManually,
    refetchCustomer,
    refetchSubscriptions,
    refetchSites: refetchBillingSites,
    refetchBillingContact,
    refetchAllData,
    setSelectedCurrency,
    setBillingCycle,
    refetchBillingMode,
  };

  return (
    <BillingContext.Provider value={context}>
      <Elements stripe={stripePromise}>{children}</Elements>
    </BillingContext.Provider>
  );
}

export function useBilling() {
  return useContext(BillingContext);
}
